// ===============================================================
// NativeChatRouteBoundary.js — PHASE 11C-1 (+ CORRECTION PASS).
// ===============================================================
//
// The narrowly-scoped React boundary mounted in place of <ChatRoomPage/>
// for the two canonical inner-chat routes (/chat/:chatId, /chat/new/:chatId
// - see App.js). ChatRoomPage itself is never rewritten, restyled, or
// duplicated here - it remains the complete, unchanged fallback.
//
// Gated behind BOTH REACT_APP_NATIVE_CHAT and the existing
// REACT_APP_NATIVE_BOTTOM_NAV_SHELL build-time flags (see
// nativeChatFeatureFlags.js) - when either is not exactly "true", this
// component makes ZERO plugin calls and renders ChatRoomPage immediately.
//
// PROTOCOL (see android/.../notifications/NativeChatRoutePlugin.java and
// NativeChatOwnershipDecision.java for the native side):
//   1. Read the CURRENT shellSessionId/sessionGeneration/routeRevision
//      from NativeShellController's own established session (never a
//      second, parallel session concept - see useNativeChatHandoffIdentity).
//   2. Request native chat ownership for the current relative route,
//      UNLESS this exact (relativeRoute, shellSessionId, sessionGeneration,
//      routeRevision) identity was already released/preempted by native -
//      see SAME-ROUTE REACQUISITION SUPPRESSION below.
//   3. While pending, and once accepted, render ONLY a neutral,
//      non-interactive placeholder - never ChatRoomPage, never a
//      redirect/history mutation, never a duplicated listener.
//   4. On rejection (or once flags are found disabled, or the plugin is
//      unavailable/errors), render ChatRoomPage exactly as it always has,
//      with its own original props/route params/location/history
//      untouched.
//   5. On unmount/route-change/identity-change, release the lease this
//      instance was holding (if any) and invalidate any in-flight
//      request so a late resolve/reject can never apply.
//   6. A native-initiated release nudge (Notification Center taking over
//      content ownership) is treated the same as losing the lease -
//      falls back to ChatRoomPage rather than silently retrying.
//   7. A native-origin navigation event (OUTER_MESSAGES/USER_PROFILE),
//      once validated, performs exactly one React Router navigation and
//      releases the lease it was issued for.
//
// SAME-ROUTE REACQUISITION SUPPRESSION (correction pass):
// The release-nudge listener below is a SEPARATE, mount-lifetime-only
// effect from the request effect - by construction, clearing
// leaseIdRef/calling setStatus() from that listener does NOT, by itself,
// re-run the request effect (its own dependency array - location.pathname
// and the three identity fields - never changes merely because a nudge
// arrived). This already prevents the literal loop ("released -> instantly
// re-requests for the identical route"). This module additionally makes
// that suppression EXPLICIT and independently robust (never relying
// solely on "which effect happens to depend on what"): the exact
// (relativeRoute, shellSessionId, sessionGeneration, routeRevision)
// identity a release nudge fires for is recorded in
// lastReleasedIdentityRef, and the request effect itself checks this
// FIRST, before ever calling requestNativeChatOwnership again - so even a
// future refactor that made the request effect re-run for some unrelated
// reason (e.g. a new dependency) could still never re-request the exact
// identity that was just released. Suppression is naturally cleared the
// moment ANY of the four identity fields actually changes (a real route
// transition, session change, or generation change) - never by a bare
// re-render alone, and never by an explicit "clear" call, since none
// exists: a changed identity simply no longer matches what was recorded.

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ChatRoomPage from "./ChatRoomPage";
import { useNativeChatHandoffIdentity } from "./NativeShellController";
import { isNativeChatFeatureEnabled } from "../navigation/nativeChatFeatureFlags";
import {
  isNativeChatRouteAvailable,
  requestNativeChatOwnership,
  releaseNativeChatOwnership,
  addNativeChatOwnershipReleasedListener,
  addNativeChatNavigationRequestedListener,
  NATIVE_CHAT_DESTINATION,
} from "../plugins/NativeChatRoute";
import { isSafeOpaqueUidSegment, encodeUserProfilePathSegment } from "../navigation/nativeChatProfileUidSegment";

const STATUS_FALLBACK = "fallback";
const STATUS_PENDING = "pending";
const STATUS_ACCEPTED = "accepted";

const OUTER_MESSAGES_ROUTE = "/messages";

function isSameIdentity(a, b) {
  if (!a || !b) {
    return false;
  }
  return (
    a.relativeRoute === b.relativeRoute &&
    a.shellSessionId === b.shellSessionId &&
    a.sessionGeneration === b.sessionGeneration &&
    a.routeRevision === b.routeRevision
  );
}

export default function NativeChatRouteBoundary() {
  const location = useLocation();
  const navigate = useNavigate();
  const identity = useNativeChatHandoffIdentity();

  const [status, setStatus] = useState(() =>
    isNativeChatFeatureEnabled() ? STATUS_PENDING : STATUS_FALLBACK
  );

  const leaseIdRef = useRef(null);
  // Bumped on every effect (re)run and every native-initiated release -
  // the sole "is this specific request still the one that matters" token,
  // mirroring NativeShellController.js's own isStillCurrent() pattern.
  const requestTokenRef = useRef(0);
  // The exact identity a native-initiated release/preemption most
  // recently fired for - see the SAME-ROUTE REACQUISITION SUPPRESSION
  // doc comment above.
  const lastReleasedIdentityRef = useRef(null);
  // Always-fresh mirrors for the mount-lifetime-only listener effects
  // below, which must never close over a stale location/identity - see
  // NativeShellController.js's own established use of refs for this
  // exact reason.
  const currentIdentityRef = useRef(null);
  // BROWSER-URL REPRESENTATION CORRECTION (PHASE 11C-1, final round):
  // location.pathname is the browser's/React Router's own RAW pathname -
  // proven by a real (non-mocked) BrowserRouter test to be percent-encoded
  // when a Unicode navigate() argument was used (see
  // NativeChatRouteBoundary.realBrowserRouter.test.js) - never React
  // Router's own decoded useParams() value, which this component
  // deliberately never reads for this purpose. Forwarded to native
  // UNCHANGED - ChatRouteParser.java owns the single authoritative
  // percent-decode of Mode.NEW's own id segment, never this component.
  currentIdentityRef.current = {
    relativeRoute: location.pathname,
    shellSessionId: identity.shellSessionId,
    sessionGeneration: identity.sessionGeneration,
    routeRevision: identity.routeRevision,
  };
  // Deduplicates native-origin navigation events by eventId - a Set
  // scoped to this component instance's own lifetime (cleared naturally
  // on unmount along with the ref itself).
  const consumedNavigationEventIdsRef = useRef(new Set());

  const releaseCurrentLease = useCallback(() => {
    const leaseId = leaseIdRef.current;
    if (!leaseId) {
      return;
    }
    leaseIdRef.current = null;
    void releaseNativeChatOwnership(leaseId);
  }, []);

  useEffect(() => {
    const myToken = ++requestTokenRef.current;
    const requestedIdentity = currentIdentityRef.current;

    if (!isNativeChatFeatureEnabled() || !isNativeChatRouteAvailable() || !identity.shellSessionId) {
      setStatus(STATUS_FALLBACK);
      return undefined;
    }

    if (isSameIdentity(lastReleasedIdentityRef.current, requestedIdentity)) {
      // This EXACT identity was already released/preempted by native -
      // never automatically re-request it. A genuinely new identity
      // (route/session/generation change) always reaches here instead,
      // since it will never match what was recorded.
      setStatus(STATUS_FALLBACK);
      return undefined;
    }

    setStatus(STATUS_PENDING);

    void (async () => {
      const result = await requestNativeChatOwnership(requestedIdentity);

      if (myToken !== requestTokenRef.current) {
        // Superseded (route/identity changed, or unmounted) while this
        // request was in flight - never apply a stale result. If it was
        // somehow accepted anyway, release it immediately (best-effort) -
        // nothing will ever consume/render for it.
        if (result.accepted) {
          void releaseNativeChatOwnership(result.leaseId);
        }
        return;
      }

      if (result.accepted) {
        leaseIdRef.current = result.leaseId;
        setStatus(STATUS_ACCEPTED);
      } else {
        leaseIdRef.current = null;
        setStatus(STATUS_FALLBACK);
      }
    })();

    return () => {
      // Invalidates THIS effect run's own in-flight request (a fresh
      // token is issued the next time the effect body runs, including a
      // React StrictMode double-invoke) - never removes the listeners
      // below, which are separate, mount-lifetime-only effects.
      requestTokenRef.current += 1;
      releaseCurrentLease();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, identity.shellSessionId, identity.sessionGeneration, identity.routeRevision]);

  // Mount-lifetime-only: native-initiated release (Notification Center
  // taking over content ownership never navigates the web route away
  // from /chat/:chatId, so the effect above would otherwise never
  // re-evaluate - see NativeChatRoutePlugin.notifyOwnershipReleasedByNative's
  // own doc comment for why this nudge exists at all).
  useEffect(() => {
    if (!isNativeChatFeatureEnabled() || !isNativeChatRouteAvailable()) {
      return undefined;
    }
    const handle = addNativeChatOwnershipReleasedListener((payload) => {
      // A stale release for a lease this instance no longer holds (an
      // older, already-superseded lease of ITS OWN, or one that never
      // belonged to it at all) must never suppress or close the CURRENT
      // lease - see this module's own doc comment, Phase 3.6.
      if (!leaseIdRef.current || payload?.leaseId !== leaseIdRef.current) {
        return;
      }
      lastReleasedIdentityRef.current = currentIdentityRef.current;
      leaseIdRef.current = null;
      requestTokenRef.current += 1; // invalidate our own in-flight/completed request identity
      setStatus(STATUS_FALLBACK);
    });
    return () => {
      void handle?.remove?.();
    };
  }, []);

  // Mount-lifetime-only: native-origin navigation events (Header
  // Back/Delete-success/Profile-tap, once a future controller wires them
  // - see NativeChatRoutePlugin.java's own doc comment for the exact
  // closed schema/validation this mirrors).
  useEffect(() => {
    if (!isNativeChatFeatureEnabled() || !isNativeChatRouteAvailable()) {
      return undefined;
    }
    const handle = addNativeChatNavigationRequestedListener((event) => {
      if (!event || typeof event.eventId !== "string" || !event.eventId) {
        return;
      }
      if (consumedNavigationEventIdsRef.current.has(event.eventId)) {
        return; // duplicate delivery - never navigates twice.
      }

      const activeLeaseId = leaseIdRef.current;
      const current = currentIdentityRef.current;
      const matchesCurrentAcceptedOwnership =
        activeLeaseId != null &&
        event.leaseId === activeLeaseId &&
        event.shellSessionId === current.shellSessionId &&
        event.sessionGeneration === current.sessionGeneration &&
        event.routeRevision === current.routeRevision;

      if (!matchesCurrentAcceptedOwnership) {
        // Wrong/old lease, wrong session, wrong generation, or a stale
        // revision (including an event arriving before acceptance or
        // after this exact lease was already released) - never navigates.
        consumedNavigationEventIdsRef.current.add(event.eventId);
        return;
      }

      let target = null;
      if (event.destination === NATIVE_CHAT_DESTINATION.OUTER_MESSAGES) {
        target = OUTER_MESSAGES_ROUTE;
      } else if (event.destination === NATIVE_CHAT_DESTINATION.USER_PROFILE) {
        if (isSafeOpaqueUidSegment(event.targetUid)) {
          // Encoded exactly once, matching this exact route's own
          // established convention elsewhere in this codebase (e.g.
          // UsersPage.js's own `/user/${encodeURIComponent(uid)}`) - see
          // nativeChatProfileUidSegment.js's own doc comment for why this
          // round-trips correctly through UserProfilePage.js's
          // useParams() regardless of the uid's own Unicode content.
          target = `/user/${encodeUserProfilePathSegment(event.targetUid)}`;
        }
      }

      consumedNavigationEventIdsRef.current.add(event.eventId);
      if (!target) {
        // Fails safe - never a partial/guessed navigation, never a second
        // content owner: the lease is still released below regardless,
        // exactly like a normal exit would.
        releaseCurrentLease();
        return;
      }

      // Release BEFORE navigating - the route change this navigate()
      // call causes will unmount this boundary anyway (matches every
      // other route-change release path), but releasing explicitly here
      // means native's own content mode returns to WEB immediately,
      // never waiting on React's own unmount timing.
      releaseCurrentLease();
      navigate(target);
    });
    return () => {
      void handle?.remove?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, releaseCurrentLease]);

  if (status === STATUS_FALLBACK) {
    return <ChatRoomPage />;
  }

  // PENDING and ACCEPTED both render the SAME neutral, non-interactive
  // placeholder - the accept decision is local/synchronous on native's
  // own side (never waits on Firestore/network/callables), so this
  // window is expected to be extremely short; ChatRoomPage is never
  // mounted underneath either state, and no redirect/history mutation
  // ever happens here.
  return <div data-testid="native-chat-route-boundary-placeholder" aria-hidden="true" />;
}
